/* eslint-disable complexity */

/* eslint-disable @typescript-eslint/naming-convention */
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'src/workspace/axios';
import { DEFAULT_NOTE_FETCH_LIMIT, NOTES_API } from './constants';
import {
  clearNotes,
  pushMoreNotes,
  pushMoreThreadNotes,
  setError,
  setLoading,
  setLoadingMore,
  setLoadingMoreError,
  setNotes,
  setThreadError,
  setThreadLoading,
  setThreadNotes,
  setUsers,
} from './slice';
import {
  CreateNotePayload,
  FetchNotesPayload,
  FetchUsersPayload,
} from './types';

const setFetchLoading = (
  dispatch: any,
  { parent_key, cursor }: FetchNotesPayload,
  loading: boolean,
) => {
  if (parent_key) {
    dispatch(setThreadLoading({ parentKey: parent_key, loading }));
  } else if (cursor) {
    dispatch(setLoadingMore(loading));
  } else {
    dispatch(setLoading(loading));
  }
};

const setFetchError = (
  dispatch: any,
  { parent_key, cursor }: FetchNotesPayload,
  error: string,
) => {
  if (parent_key) {
    dispatch(setThreadError({ parentKey: parent_key, error }));
  } else if (cursor) {
    dispatch(setLoadingMoreError(error));
  } else {
    dispatch(setError(error));
  }
};

export const fetchNotes = createAsyncThunk(
  'notes/all',
  async (payload: FetchNotesPayload, { dispatch }) => {
    const { vo_id, parent_key, limit, cursor } = payload;

    try {
      setFetchLoading(dispatch, payload, true);
      setFetchError(dispatch, payload, '');

      const params: Record<string, any> = {
        vo_id,
        limit,
      };
      if (parent_key) {
        params.parent_key = parent_key;
      }
      if (cursor) {
        params.cursor = cursor;
      }
      const { data } = await axios.get(NOTES_API.GET_ALL, {
        params,
      });

      const { pagination, record_sets } = data;
      if (parent_key) {
        if (cursor) {
          dispatch(
            pushMoreThreadNotes({ parentKey: parent_key, notes: record_sets }),
          );
        } else {
          dispatch(
            setThreadNotes({ parentKey: parent_key, notes: record_sets }),
          );
        }
      } else if (cursor) {
        dispatch(pushMoreNotes(record_sets));
      } else {
        dispatch(setNotes(record_sets));
      }

      if (pagination?.next?.cursor) {
        dispatch(
          fetchNotes({
            vo_id,
            parent_key,
            limit,
            cursor: pagination.next.cursor,
          }),
        );
      }
    } catch (e: any) {
      setFetchError(dispatch, payload, e.message || 'Error');
    } finally {
      setFetchLoading(dispatch, payload, false);
    }
  },
);

const fetchUsers = createAsyncThunk(
  'notes/users',
  async ({ vo_id }: FetchUsersPayload, { dispatch }) => {
    try {
      const { data } = await axios.get(NOTES_API.USERS, {
        params: {
          vo_id,
        },
      });
      dispatch(setUsers(data.record_sets || []));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  },
);

export const createNote = createAsyncThunk(
  'notes/create',
  async (payload: CreateNotePayload, { dispatch }) => {
    try {
      const { vo_id, parent_key } = payload;
      await axios.post(NOTES_API.CREATE, payload);
      dispatch(
        fetchNotes({
          vo_id,
          limit: DEFAULT_NOTE_FETCH_LIMIT,
          parent_key,
        }),
      );
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return false;
    }
  },
);

export const onActivate = ({ dispatch, props }) => {
  const { vo_id } = props;
  dispatch(clearNotes());
  dispatch(fetchNotes({ vo_id, limit: DEFAULT_NOTE_FETCH_LIMIT }));
  dispatch(fetchUsers({ vo_id }));
};

export const onDeactivate = () => {};

export default { onActivate, onDeactivate };
